import apiClient from "../../apiClient";
// Types
export interface PackageType {
  id: string;
  name: string;
  betAmountPerPoint: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AgentPlayerAssignment {
  id: string;
  agentId: string;
  playerId: string;
  packageTypeId: string;
  agentType: "NORMAL" | "JUNKET";
  commissionPercentage?: number;
  sharePercentage?: number;
  lossCommissionSplit?: { agent: number; player: number };
  rollingCommissionSplit?: { agent: number; player: number };
  depositCommissionSplit?: { agent: number; player: number };
  maxBetLimit: number;
  facilitiesAgreed: boolean;
  facilitiesConditions?: string;
  facilitiesCondition?: "ON_LOSS" | "GUARANTEED" | "CONDITIONAL";
  totalLossThreshold?: number;
  previousAssignmentId?: string;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Related data
  agentName?: string;
  playerName?: string;
  packageName?: string;
}

export interface CreateAgentPlayerAssignment {
  agentId: string;
  playerId: string;
  packageTypeId: string;
  agentType: "NORMAL" | "JUNKET";
  commissionPercentage?: number;
  sharePercentage?: number;
  lossCommissionSplit?: { agent: number; player: number };
  rollingCommissionSplit?: { agent: number; player: number };
  depositCommissionSplit?: { agent: number; player: number };
  maxBetLimit: number;
  facilitiesAgreed?: boolean;
  facilitiesConditions?: string;
  facilitiesCondition?: "ON_LOSS" | "GUARANTEED" | "CONDITIONAL";
  totalLossThreshold?: number;
  startDate: string;
}

export interface UpdateAgentPlayerAssignment {
  commissionPercentage?: number;
  sharePercentage?: number;
  lossCommissionSplit?: { agent: number; player: number };
  rollingCommissionSplit?: { agent: number; player: number };
  depositCommissionSplit?: { agent: number; player: number };
  maxBetLimit?: number;
  facilitiesAgreed?: boolean;
  facilitiesConditions?: string;
  facilitiesCondition?: "ON_LOSS" | "GUARANTEED" | "CONDITIONAL";
  totalLossThreshold?: number;
  endDate?: string;
}

export interface ReviseAssignmentRequest {
  reason: string;
  newAgentType?: "NORMAL" | "JUNKET";
  newCommissionPercentage?: number;
  newSharePercentage?: number;
  newMaxBetLimit?: number;
  newCommissionSplits?: {
    loss?: { agent: number; player: number };
    rolling?: { agent: number; player: number };
    deposit?: { agent: number; player: number };
  };
}

export interface AssignmentFilters {
  agentId?: string;
  playerId?: string;
  agentType?: "NORMAL" | "JUNKET";
  packageTypeId?: string;
  isActive?: boolean;
}

class CommissionService {
  private baseUrl = "";

  // Package Types
  async getPackageTypes(): Promise<PackageType[]> {
    const response = await apiClient.get(`${this.baseUrl}/package-types`);
    const data = response.data?.data || [];

    // Transform snake_case to camelCase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return data.map((pkg: any) => ({
      id: pkg.id,
      name: pkg.name,
      betAmountPerPoint: parseFloat(pkg.bet_amount_per_point),
      description: pkg.description,
      isActive: pkg.is_active,
      createdAt: pkg.created_at,
      updatedAt: pkg.updated_at,
    }));
  }

  async createPackageType(data: {
    name: string;
    betAmountPerPoint: number;
    description?: string;
  }): Promise<PackageType> {
    const response = await apiClient.post(
      `${this.baseUrl}/package-types`,
      data,
    );
    return response.data?.data;
  }

  async getPackageType(id: string): Promise<PackageType> {
    const response = await apiClient.get(`${this.baseUrl}/package-types/${id}`);
    return response.data?.data;
  }

  async updatePackageType(
    id: string,
    data: {
      name?: string;
      betAmountPerPoint?: number;
      description?: string;
      isActive?: boolean;
    },
  ): Promise<PackageType> {
    // Convert camelCase to snake_case for API
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const requestData: any = {};
    if (data.name !== undefined) requestData.name = data.name;
    if (data.betAmountPerPoint !== undefined)
      requestData.bet_amount_per_point = data.betAmountPerPoint;
    if (data.description !== undefined)
      requestData.description = data.description;
    if (data.isActive !== undefined) requestData.is_active = data.isActive;

    const response = await apiClient.put(
      `${this.baseUrl}/package-types/${id}`,
      requestData,
    );

    const pkg = response.data?.data;
    return {
      id: pkg.id,
      name: pkg.name,
      betAmountPerPoint: parseFloat(pkg.bet_amount_per_point),
      description: pkg.description,
      isActive: pkg.is_active,
      createdAt: pkg.created_at,
      updatedAt: pkg.updated_at,
    };
  }

  async deletePackageType(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/package-types/${id}`);
  }

  // Agent Player Assignments
  async getAgentAssignments(
    agentId: string,
    activeOnly: boolean = true,
  ): Promise<AgentPlayerAssignment[]> {
    const response = await apiClient.get(
      `${this.baseUrl}/agents/${agentId}/assignments?activeOnly=${activeOnly}`,
    );
    return response.data?.data || [];
  }

  async getAssignments(
    filters?: AssignmentFilters,
  ): Promise<AgentPlayerAssignment[]> {
    const params = new URLSearchParams();
    if (filters?.agentId) params.append("agentId", filters.agentId);
    if (filters?.playerId) params.append("playerId", filters.playerId);
    if (filters?.agentType) params.append("agentType", filters.agentType);
    if (filters?.packageTypeId)
      params.append("packageTypeId", filters.packageTypeId);
    if (filters?.isActive !== undefined)
      params.append("isActive", filters.isActive.toString());

    const response = await apiClient.get(
      `${this.baseUrl}/assignments?${params.toString()}`,
    );
    return response.data?.data || [];
  }

  async getAssignment(id: string): Promise<AgentPlayerAssignment> {
    const response = await apiClient.get(`${this.baseUrl}/assignments/${id}`);
    return response.data?.data;
  }

  async createAssignment(
    data: CreateAgentPlayerAssignment,
  ): Promise<AgentPlayerAssignment> {
    const response = await apiClient.post(`${this.baseUrl}/assignments`, data);
    return response.data?.data;
  }

  async updateAssignment(
    id: string,
    data: UpdateAgentPlayerAssignment,
  ): Promise<AgentPlayerAssignment> {
    const response = await apiClient.put(
      `${this.baseUrl}/assignments/${id}`,
      data,
    );
    return response.data?.data;
  }

  async reviseAssignment(
    id: string,
    data: ReviseAssignmentRequest,
  ): Promise<AgentPlayerAssignment> {
    const response = await apiClient.post(
      `${this.baseUrl}/assignments/${id}/revise`,
      data,
    );
    return response.data?.data;
  }

  // Utility methods
  async getCommissionSummary(agentId: string) {
    const assignments = await this.getAgentAssignments(agentId);

    const totalPlayers = assignments.length;
    const activePlayers = assignments.filter((a) => a.isActive).length;

    // Group by package type
    const packageGroups = assignments.reduce(
      (acc, assignment) => {
        const packageName = assignment.packageName || "Unknown";
        if (!acc[packageName]) {
          acc[packageName] = [];
        }
        acc[packageName].push(assignment);
        return acc;
      },
      {} as Record<string, AgentPlayerAssignment[]>,
    );

    return {
      totalPlayers,
      activePlayers,
      packageGroups,
      totalCommissionEarned: 0, // TODO: Calculate from actual commission records
      pendingCommission: 0, // TODO: Calculate from pending commission records
      assignments,
    };
  }

  // Get standard commission rates by package
  getStandardRates() {
    return {
      Normal: {
        loss: { agent: 5.0, player: 2.0 },
        rolling: { agent: 0.3, player: 0.2 },
        deposit: { agent: 2.0, player: 1.0 },
        betAmountPerPoint: 5000,
      },
      Rolling: {
        loss: { agent: 8.0, player: 3.0 },
        rolling: { agent: 0.5, player: 0.3 },
        deposit: { agent: 3.0, player: 2.0 },
        betAmountPerPoint: 10000,
      },
      Junket: {
        loss: { agent: 10.0, player: 5.0 },
        rolling: { agent: 0.8, player: 0.4 },
        deposit: { agent: 4.0, player: 3.0 },
        betAmountPerPoint: 15000,
      },
    };
  }

  // Format currency for display
  formatCurrency(amount: number, currency: string = "INR"): string {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  // Calculate points based on package
  calculatePoints(amount: number, betAmountPerPoint: number): number {
    return Math.floor(amount / betAmountPerPoint);
  }

  // Calculate commission preview
  calculateCommissionPreview(
    assignment: AgentPlayerAssignment,
    amounts: { betting?: number; loss?: number; deposit?: number },
  ) {
    const standardRates = this.getStandardRates();
    const packageName = assignment.packageName as keyof typeof standardRates;
    const rates = standardRates[packageName] || standardRates.Normal;

    // Use assignment-specific rates or defaults
    const lossRates = assignment.lossCommissionSplit || rates.loss;
    const rollingRates = assignment.rollingCommissionSplit || rates.rolling;
    const depositRates = assignment.depositCommissionSplit || rates.deposit;

    const result = {
      agentCommission: 0,
      playerRoyalty: 0,
      shareAmount: 0,
      totalPayout: 0,
      points: 0,
    };

    // Calculate based on provided amounts
    if (amounts.loss) {
      result.agentCommission += (amounts.loss * lossRates.agent) / 100;
      result.playerRoyalty += (amounts.loss * lossRates.player) / 100;
      result.points += this.calculatePoints(
        amounts.loss,
        rates.betAmountPerPoint,
      );
    }

    if (amounts.betting) {
      result.agentCommission += (amounts.betting * rollingRates.agent) / 100;
      result.playerRoyalty += (amounts.betting * rollingRates.player) / 100;
      result.points += this.calculatePoints(
        amounts.betting,
        rates.betAmountPerPoint,
      );
    }

    if (amounts.deposit) {
      result.agentCommission += (amounts.deposit * depositRates.agent) / 100;
      result.playerRoyalty += (amounts.deposit * depositRates.player) / 100;
    }

    // Add share for JUNKET agents
    if (
      assignment.agentType === "JUNKET" &&
      assignment.sharePercentage &&
      amounts.loss
    ) {
      result.shareAmount = (amounts.loss * assignment.sharePercentage) / 100;
    }

    result.totalPayout =
      result.agentCommission + result.playerRoyalty + result.shareAmount;

    return result;
  }
}

export const commissionService = new CommissionService();
